import { memo, SyntheticEvent, useCallback } from 'react'
import { useField, useForm } from 'react-final-form'
import { FormItem, IconButton, Label } from '@cargomart/ui'
import cn from 'classnames'
import { FieldSelectServiceType } from 'forms/FieldSelectServiceType'
import { compose, minMax, required, trimRequired } from 'forms/validators'
import IconPlus from 'remixicon/icons/System/add-line.svg?react'
import IconDelete from 'remixicon/icons/System/delete-bin-7-fill.svg?react'
import IconMinus from 'remixicon/icons/System/subtract-fill.svg?react'
import { OrderCreateCargo } from 'schema'
import { FieldNumber } from 'shared/forms/FieldNumber'
import { FieldText } from 'shared/forms/FieldText'
import { useFieldArray } from 'shared/forms/useFieldArray'

import { TOrderGuestCreateCargoForm } from '../../types/orderGuestCreateTypes'
import { MAX_AMOUNT, MAX_AMOUNT_GRADE, MAX_PRICE, MAX_PRICE_GRADE, MIN_AMOUNT, MIN_PRICE } from './constants'
import { OrderCargoSeparateItemSize } from './OrderCargoSeparateItemSize'

interface IProps {
  index: number
  name: string
  className?: string
  onOpenCustomScheduler?: () => void
  onOpenPalletScheduler?: () => void
}

export const OrderCargoSeparateItem = memo<IProps>(function OrderCargoSeparateItem({
  className,
  name,
  index,
  onOpenCustomScheduler,
  onOpenPalletScheduler
}) {
  const fieldClassName = 'w-full [&>*:first-child]:!text-on-surface [&>*]:!text-on-surface-variant'
  const form = useForm()
  const {
    fields: { remove, length = 0 }
  } = useFieldArray<TOrderGuestCreateCargoForm[]>('cargo')
  const {
    input: { value: type }
  } = useField<TOrderGuestCreateCargoForm['type']>(`${name}.type`)
  const {
    input: { value: amount }
  } = useField<TOrderGuestCreateCargoForm['amount']>(`${name}.amount`)

  const handleRemove = useCallback(() => remove(index), [index, remove])

  const handleOpenScheduler = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault()

      if (type === OrderCreateCargo.type.PALLET) {
        onOpenPalletScheduler?.()
      } else {
        onOpenCustomScheduler?.()
      }
    },
    [onOpenPalletScheduler, onOpenCustomScheduler, type]
  )

  const handleIncrementAmount = useCallback(() => {
    form.change(`${name}.amount`, amount + 1)
  }, [amount, form, name])

  const handleDecrementAmount = useCallback(() => {
    form.change(`${name}.amount`, amount - 1)
  }, [amount, form, name])

  return (
    <div className={cn(className, 'rounded-md border border-solid border-outline p-3')}>
      <div className="flex min-h-[32px] items-center justify-between">
        <div className="text-title-md">Место №{index + 1}</div>
        {length > 1 && (
          <IconButton outline onClick={handleRemove}>
            <IconDelete size="sm" />
          </IconButton>
        )}
      </div>
      <div className="grid grid-cols-1 gap-x-4 @xs:grid-cols-2">
        <FormItem label={<Label className="pt-2">Описание груза</Label>}>
          <FieldText isShowTooltip className={fieldClassName} name={`${name}.about`} validate={trimRequired} />
        </FormItem>
        <FormItem
          label={
            <Label className="pt-2">
              Тариф (
              <a className="link cursor-pointer" href="#" onClick={handleOpenScheduler}>
                условия
              </a>
              )
            </Label>
          }
        >
          <FieldSelectServiceType name={`${name}.type`} />
        </FormItem>
      </div>

      <OrderCargoSeparateItemSize name={name} type={type} />

      <div className="grid grid-cols-1 gap-x-4 @sm:grid-cols-2">
        <FormItem label={<Label className="pt-2">Объявленная ценность</Label>}>
          <FieldNumber
            isShowTooltip
            className={fieldClassName}
            max={MAX_PRICE_GRADE}
            name={`${name}.price`}
            postfix="₽"
            scale={2}
            validate={compose(required, minMax(MIN_PRICE, MAX_PRICE, '₽'))}
          />
        </FormItem>
        <FormItem label={<Label className="pt-2">Одинаковых мест</Label>}>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-1">
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_AMOUNT_GRADE}
              min={1}
              name={`${name}.amount`}
              postfix="шт"
              validate={compose(required, minMax(MIN_AMOUNT, MAX_AMOUNT, 'шт'))}
            />
            <IconButton outline disabled={amount <= 1} onClick={handleDecrementAmount}>
              <IconMinus size="xs" />
            </IconButton>
            <IconButton outline disabled={amount >= MAX_AMOUNT} onClick={handleIncrementAmount}>
              <IconPlus size="xs" />
            </IconButton>
          </div>
        </FormItem>
      </div>
    </div>
  )
})
